import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

const API_URL = 'https://ufb-1.onrender.com/api/inventory';

const SIZES = ['XS', 'S', 'M', 'L', 'XL', 'XXL'];

const EditInventory = () => {
  const { id } = useParams();
  const [form, setForm] = useState({ 
    productName: '', 
    gsm: '', 
    stockBySize: {},
    stockThresholds: {}
  });
  const [currentImage, setCurrentImage] = useState(null);
  const [newImage, setNewImage] = useState(null);
  const [newImagePreview, setNewImagePreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [newSize, setNewSize] = useState('M');
  const [newStock, setNewStock] = useState('');
  const [newThreshold, setNewThreshold] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchItem = async () => {
      try {
        const res = await fetch(`${API_URL}/${id}`);
        if (!res.ok) {
          const errorData = await res.json();
          throw new Error(errorData.error || 'Failed to fetch item');
        }
        const data = await res.json();
        setForm({
          productName: data.productName || '',
          gsm: data.gsm || '',
          stockBySize: data.stockBySize || {},
          stockThresholds: data.stockThresholds || {}
        });
        setCurrentImage(data.imageUrl);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchItem();
  }, [id]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const addSize = () => {
    if (!newSize || newStock === '') {
      setError('Please select a size and enter stock amount');
      return;
    }

    if (Object.keys(form.stockBySize).includes(newSize)) {
      setError(`Size ${newSize} is already added`);
      return;
    }

    setForm({
      ...form,
      stockBySize: { ...form.stockBySize, [newSize]: Number(newStock) },
      stockThresholds: { ...form.stockThresholds, [newSize]: Number(newThreshold) || 0 }
    });
    setNewStock('');
    setNewThreshold('');
    setError(null);

    // Update newSize to the next available size (if any)
    const nextAvailable = availableSizes.filter(size => size !== newSize)[0] || '';
    setNewSize(nextAvailable);
  };

  const removeSize = (size) => {
    const newStockBySize = { ...form.stockBySize };
    const newStockThresholds = { ...form.stockThresholds };
    delete newStockBySize[size];
    delete newStockThresholds[size];
    setForm({ ...form, stockBySize: newStockBySize, stockThresholds: newStockThresholds });
  };

  const updateSizeStock = (size, stockCount) => {
    setForm({
      ...form,
      stockBySize: { ...form.stockBySize, [size]: Number(stockCount) }
    });
  };

  const updateSizeThreshold = (size, threshold) => {
    setForm({
      ...form,
      stockThresholds: { ...form.stockThresholds, [size]: Number(threshold) }
    });
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setNewImage(file);
      setNewImagePreview(URL.createObjectURL(file));
    } else {
      setNewImage(null);
      setNewImagePreview(null);
    }
  };

  const uploadImage = async () => {
    if (!newImage) return;
    const formData = new FormData();
    formData.append('image', newImage);
    try {
      const res = await fetch(`${API_URL}/${id}/image`, {
        method: 'POST',
        body: formData
      });
      if (!res.ok) throw new Error('Failed to upload image');
      const data = await res.json();
      if (data.imageUrl) setCurrentImage(data.imageUrl);
      setNewImage(null);
    } catch (err) {
      setError('Image upload failed: ' + err.message);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSaving(true);
    
    if (Object.keys(form.stockBySize).length === 0) {
      setError('Please add at least one size');
      setSaving(false);
      return;
    }

    try {
      // Update the product
      const res = await fetch(`${API_URL}/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          productName: form.productName,
          gsm: form.gsm,
          stockBySize: form.stockBySize,
          stockThresholds: form.stockThresholds
        })
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || 'Failed to update product');
      }

      // If a new image is selected, upload it
      if (newImage) {
        await uploadImage();
      }

      const result = await res.json();
      
      if (result.success) {
        navigate('/list');
      } else {
        throw new Error('Failed to update product');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="flex justify-center items-center h-64"><div className="text-xl">Loading...</div></div>;
  if (error) return <div className="text-red-500 text-center">{error}</div>;

  const availableSizes = SIZES.filter(size => !Object.keys(form.stockBySize).includes(size));

  return (
    <div className="max-w-2xl mx-auto p-10 bg-white rounded-2xl shadow-2xl mt-10 border border-yellow-100">
      <h2 className="text-3xl font-extrabold mb-6 text-center text-yellow-700 tracking-tight">Edit Product</h2>
      
      <form onSubmit={handleSubmit} className="flex flex-col gap-6">
        {/* Product Details */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <input 
            name="productName" 
            value={form.productName} 
            onChange={handleChange} 
            placeholder="Product Name" 
            className="border-2 border-yellow-200 p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400 bg-slate-50 text-yellow-900 font-medium" 
            required 
          />
          <input 
            name="gsm" 
            value={form.gsm} 
            onChange={handleChange} 
            placeholder="GSM" 
            className="border-2 border-yellow-200 p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400 bg-slate-50 text-yellow-900 font-medium" 
            required 
          />
        </div>

        {/* Current Image Display and Replace */}
        {currentImage && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Current Product Image</label>
            <img 
              src={currentImage} 
              alt="Current" 
              className="w-32 h-32 object-cover rounded-lg border"
            />
          </div>
        )}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Replace Image</label>
          <input 
            type="file" 
            accept="image/*" 
            onChange={handleImageSelect}
            className="border-2 border-yellow-200 p-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400 bg-slate-50"
          />
          {newImage && (
            <p className="text-sm text-green-600 mt-1">✓ {newImage.name} selected</p>
          )}
          {newImagePreview && (
            <div className="mt-3">
              <img src={newImagePreview} alt="Preview" className="w-32 h-32 object-cover rounded-lg border mx-auto" />
              <p className="text-xs text-gray-500 text-center mt-1">New Image Preview</p>
            </div>
          )}
        </div>

        {/* Sizes Section */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-4">Product Sizes & Stock Thresholds</label>
          
          {/* Add New Size */}
          <div className="flex gap-3 mb-4 p-4 bg-gray-50 rounded-lg">
            <select 
              value={newSize} 
              onChange={(e) => setNewSize(e.target.value)}
              className="border-2 border-yellow-200 p-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400 bg-white flex-1"
              disabled={availableSizes.length === 0}
            >
              {availableSizes.map(size => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
            <input 
              type="number" 
              min="0"
              value={newStock} 
              onChange={(e) => setNewStock(e.target.value)}
              placeholder="Stock" 
              className="border-2 border-yellow-200 p-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400 bg-white w-24"
            />
            <input 
              type="number" 
              min="0"
              value={newThreshold} 
              onChange={(e) => setNewThreshold(e.target.value)}
              placeholder="Threshold" 
              className="border-2 border-yellow-200 p-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400 bg-white w-24"
            />
            <button 
              type="button" 
              onClick={addSize}
              disabled={availableSizes.length === 0 || newStock === '' || !newSize}
              className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Add New
            </button>
          </div>

          {availableSizes.length === 0 && (
            <p className="text-sm text-gray-500 text-center py-2">All sizes have been added</p>
          )}
          
          {/* Existing Sizes */}
          {Object.keys(form.stockBySize).length === 0 && (
            <div className="text-center text-gray-500 py-4">No sizes added yet. Use the form above to add sizes.</div>
          )}
          
          <div className="space-y-3">
            {Object.entries(form.stockBySize).map(([size, stockCount]) => (
              <div key={size} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <span className="text-lg font-semibold text-gray-800 min-w-[40px]">{size}</span>
                <div className="flex flex-col gap-1">
                  <label className="text-xs text-gray-600">Stock</label>
                  <input 
                    type="number" 
                    min="0"
                    value={stockCount} 
                    onChange={(e) => updateSizeStock(size, e.target.value)}
                    placeholder="Stock" 
                    className="border-2 border-yellow-200 p-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400 bg-white w-24"
                    required
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-xs text-gray-600">Threshold</label>
                  <input 
                    type="number" 
                    min="0"
                    value={form.stockThresholds[size] || ''} 
                    onChange={(e) => updateSizeThreshold(size, e.target.value)}
                    placeholder="Threshold" 
                    className="border-2 border-yellow-200 p-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400 bg-white w-24"
                  />
                </div>
                <button 
                  type="button" 
                  onClick={() => removeSize(size)}
                  className="px-3 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 font-semibold"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        </div>

        {error && <div className="text-red-500 text-center font-semibold">{error}</div>}
        
        <div className="flex gap-4">
          <button 
            type="submit" 
            disabled={saving}
            className="flex-1 bg-gradient-to-r from-yellow-500 to-yellow-300 text-yellow-900 p-3 rounded-lg font-bold text-lg hover:scale-105 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Updating Product...' : 'Update Product'}
          </button>
          <button 
            type="button"
            onClick={() => navigate(-1)}
            className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditInventory;


